<template>
  <div id="sidebar-vessel-detail-wrapper" class="vs-con-loading__container">
    <div v-if="vesselDetail" id="sidebar-vessel-detail">
      <div class="h-full pt-4 sidebar-vessel-detail__container">
        <div v-if="show">
          <vs-row>
            <vs-col
              vs-offset="10"
              vs-type="flex"
              vs-justify="center"
              vs-align="center"
              vs-w="2"
            >
              <feather-icon
                icon="XIcon"
                @click.stop="cleanUpVesselDetail"
                class="cursor-pointer"
              ></feather-icon>
            </vs-col>
          </vs-row>
          <div class="ml-4 mb-2">
            <div class="w-full bg-grid-color">
              <h5 class="truncate">{{ vesselName }}</h5>
            </div>
          </div>
          <div v-show="showImage" class="ml-4 mr-4">
            <swiper
              :options="imageSwiperOption"
              :dir="$vs.rtl ? 'rtl' : 'ltr'"
              :key="`image-${$vs.rtl}`"
            >
              <swiper-slide v-for="picture in vesselPictures" :key="picture.id">
                <picture-view :picture="picture"></picture-view>
              </swiper-slide>
              <div
                v-if="vesselPictures.length > 1"
                class="swiper-button-prev swiper-button-white"
                slot="button-prev"
              ></div>
              <div
                v-if="vesselPictures.length > 1"
                class="swiper-button-next swiper-button-white"
                slot="button-next"
              ></div>
            </swiper>
          </div>
          <div v-show="showCamera" class="ml-4 mr-4">
            <swiper
              v-if="vesselCameras.length !== 0"
              :options="cameraSwiperOption"
              :dir="$vs.rtl ? 'rtl' : 'ltr'"
              :key="`camera-${$vs.rtl}`"
            >
              <swiper-slide
                v-for="vesselCamera in vesselCameras"
                :key="vesselCamera.id"
              >
                <camera-view :camera="vesselCamera" />
              </swiper-slide>
              <div
                v-if="vesselCameras.length > 1"
                class="swiper-button-prev swiper-button-white"
                slot="button-prev"
              ></div>
              <div
                v-if="vesselCameras.length > 1"
                class="swiper-button-next swiper-button-white"
                slot="button-next"
              ></div>
            </swiper>
          </div>
          <div v-show="showRadar" class="ml-4 mr-4">
            <swiper
              v-if="vesselRadars.length !== 0"
              :options="cameraSwiperOption"
              :dir="$vs.rtl ? 'rtl' : 'ltr'"
              :key="`camera-${$vs.rtl}`"
            >
              <swiper-slide
                v-for="vesselRadar in vesselRadars"
                :key="vesselRadar.id"
              >
                <radar-view :radar="vesselRadar" />
              </swiper-slide>
              <div
                v-if="vesselRadars.length > 1"
                class="swiper-button-prev swiper-button-white"
                slot="button-prev"
              ></div>
              <div
                v-if="vesselRadars.length > 1"
                class="swiper-button-next swiper-button-white"
                slot="button-next"
              ></div>
            </swiper>
          </div>

          <vs-row
            v-if="showIcon"
            vs-type="flex"
            class="mt-4 mb-4"
            vs-justify="center"
          >
            <vs-col
              vs-type="flex"
              vs-justify="center"
              vs-align="center"
              vs-w="2"
            >
              <vs-icon
                icon-pack="feather"
                icon="icon-image"
                size="20px"
                :color="imageIconColor"
                class="mr-4 cursor-pointer"
                @click.stop="onSwitchMedia('image')"
              ></vs-icon>
              <vs-icon
                icon-pack="feather"
                icon="icon-video"
                size="20px"
                :color="videoIconColor"
                class="ml-4 cursor-pointer"
                @click.stop="onSwitchMedia('camera')"
              ></vs-icon>
              <img
                :src="radarIcon"
                class="ml-8 cursor-pointer"
                width="18px"
                height="18px"
                @click.stop="onSwitchMedia('radar')"
              />
            </vs-col>
          </vs-row>

          <div class="flex items-center justify-between mt-2">
            <div class="flex items-center">
              <div class="leading-none mt-2 ml-4">
                <span>{{ $t("Views.Vessel.Destination") }}</span>
              </div>
            </div>
            <div class="leading-none mt-2 mr-4">
              <span class="font-semibold">{{ destination }}</span>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="leading-none mt-4 ml-4">
                <span>{{ $t("Views.Vessel.ETA") }}</span>
              </div>
            </div>
            <div class="leading-none mt-4 mr-4">
              <span class="font-semibold">{{ eta }}</span>
            </div>
          </div>
          <vs-divider />

          <component
            :is="scrollbarTag"
            class="sidebar-vessel-detail-scroll-area pt-4 pb-6"
            :style="styleObject"
            :settings="settings"
            :key="$vs.rtl"
          >
            <vs-row vs-type="flex" class="mt-1 mr-4 mb-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <feather-icon
                    icon="RssIcon"
                    svgClasses="h-5 w-5 mb-1 ml-4 stroke-current"
                  ></feather-icon>
                </div>
                <div class="leading-none ml-2">
                  <h6 class="font-semibold">Live</h6>
                </div>
              </div>
            </vs-row>

            <div class="flex items-center justify-between ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.Status") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ vesselOperations }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.Velocity") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ speed }} kn</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.Heading") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ heading }} °</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.Latitude") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ lat }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.Longitude") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ lng }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.LastReceived") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ lastReceived }}</span>
              </div>
            </div>

            <vs-divider class="ml-4" />

            <vs-row vs-type="flex" class="mt-1 mr-4 mb-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <feather-icon
                    icon="InfoIcon"
                    svgClasses="h-5 w-5 mb-1 ml-4 stroke-current feather-icon-color"
                  ></feather-icon>
                </div>
                <div class="leading-none ml-2">
                  <h6 class="font-semibold">{{ $t("Views.Vessel.Info") }}</h6>
                </div>
              </div>
            </vs-row>

            <div class="flex items-center justify-between ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.Country") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ countryName }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.Category") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ vesselCategoryName }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.IMO") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ imo }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.CallSign") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ callSign }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.MMSI") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ mmsi }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span
                    >{{ $t("Views.Vessel.LengthOverall") }} x
                    {{ $t("Views.Vessel.Width") }}</span
                  >
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ length }} x {{ width }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.FullDraft") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ fullDraft }}</span>
              </div>
            </div>

            <div class="flex items-center justify-between mt-4 ml-4 mr-4">
              <div class="flex items-center">
                <div class="leading-none">
                  <span>{{ $t("Views.Vessel.GrossTonnage") }}</span>
                </div>
              </div>
              <div class="leading-none">
                <span class="font-semibold">{{ grossTonnage }}</span>
              </div>
            </div>
          </component>

          <vs-divider v-if="!isOwnVessel(vesselDetail.id)" class="ml-4" />

          <div slot="footer" class="footer-scroll-area">
            <div v-if="!isFreeAccount">
              <span class="group-select-label">グループを選択</span>
              <div
                class="group-container"
                v-for="(favorite, favoriteKey) in sortedFavoriteList"
                :key="favoriteKey"
              >
                <span>{{ favorite.label }}</span>
                <div class="group-chip-area">
                  <div
                    class="group-chip"
                    :class="{
                      selected: group.vessels.includes(vesselDetail.id),
                    }"
                    v-for="(group, index) in favorite.groupList"
                    :key="index + 'group'"
                    :style="{ '--color': group.color }"
                    @click="
                      clickGroupChip(
                        vesselDetail.id,
                        favoriteKey,
                        group.documentId,
                        group.vessels.includes(vesselDetail.id),
                      )
                    "
                  >
                    <div class="chip-circle"></div>
                    <span class="chip-label">{{ group.label }}</span>
                    <div class="chip-background"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import "swiper/dist/css/swiper.min.css";
import { swiper, swiperSlide } from "vue-awesome-swiper";
import PictureView from "./components/PictureView";
import CameraView from "./components/CameraView";
import RadarView from "./components/RadarView";
import { DateUtil, GeoUtil } from "@/utils";
import { VesselMixin } from "@/mixins";
import * as Enum from "@/config/const.js";

import { createNamespacedHelpers } from "vuex";
const { mapState, mapGetters, mapActions, mapMutations } =
  createNamespacedHelpers("liveMap");

export default {
  name: "SidebarVesselDetail",
  mixins: [VesselMixin],
  components: {
    VuePerfectScrollbar,
    swiper,
    swiperSlide,
    CameraView,
    PictureView,
    RadarView,
  },
  data() {
    return {
      active: true,
      show: false,
      activeItem: "image",
      switch: false,
      monitoringVessel: false,
      imageSwiperOption: {
        height: 150,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
      },
      cameraSwiperOption: {
        height: 150,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
      },
      settings: {
        suppressScrollX: true,
        maxScrollbarLength: 60,
        wheelSpeed: 0.6,
      },
      defaultValues: {
        num: "0.0",
        chara: "---",
      },
      iconRadarOn: require("@/assets/images/icons/lolcal/radar_on.svg"),
      iconRadarOff: require("@/assets/images/icons/lolcal/radar_off.svg"),
      styleObject: {
        height: "300px",
      },
    };
  },
  computed: {
    ...mapState({
      showVesselDetail: (state) => state.showVesselDetail,
      vesselDetail: (state) => state.vesselDetail,
      vesselPictures: (state) => state.vesselPictures,
      vesselCameras: (state) => state.vesselCameras,
      vesselRadars: (state) => state.vesselRadars,
      vesselDetailInfo: (state) => state.vesselDetailInfo,
      vesselDetailPosition: (state) => state.vesselDetailPosition,
      monitoringState: (state) => state.monitoringState,
      favoriteList: (state) => state.favoriteList,
      NAVIGATION_STATUS: () => Enum.NAVIGATION_STATUS,
    }),
    ...mapGetters([
      "getCountryName",
      "isOwnVessel",
      "isMonitoring",
      "isMonitoringLimit",
    ]),
    showImage() {
      return this.activeItem === "image";
    },
    showCamera() {
      return this.activeItem === "camera";
    },
    showRadar() {
      return this.activeItem === "radar";
    },
    imageIconColor() {
      return this.activeItem === "image" ? "#626262" : "#979595";
    },
    videoIconColor() {
      return this.activeItem === "camera" ? "#626262" : "#979595";
    },
    radarIcon() {
      return this.activeItem === "radar" ? this.iconRadarOn : this.iconRadarOff;
    },
    rtl: {
      get() {
        return this.$vs.rtl;
      },
      set(val) {
        this.$vs.rtl = val;
      },
    },
    scrollbarTag() {
      return this.$store.state.is_touch_device ? "div" : "VuePerfectScrollbar";
    },
    showIcon() {
      if (this.vesselDetail) {
        return this.isOwnVessel(this.vesselDetail.id);
      } else {
        return false;
      }
    },
    vesselName() {
      if (this.vesselDetail) {
        if (
          this.vesselDetail.name === null ||
          this.vesselDetail.name === undefined
        ) {
          return `${this.vesselDetail.mmsi}(MMSI)`;
        } else {
          return this.vesselDetail.name;
        }
      } else {
        return "";
      }
    },
    destination() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.dt)
          ? this.vesselDetailInfo.dt
          : "---";
      } else {
        return "---";
      }
    },
    heading() {
      if (this.vesselDetailPosition) {
        return this.vesselDetailPosition.h !== undefined ||
          this.vesselDetailPosition.h !== 0
          ? this.vesselDetailPosition.h
          : this.vesselDetailPosition.d !== undefined ||
            this.vesselDetailPosition.d !== 0
          ? this.vesselDetailPosition.d
          : "0";
      } else {
        return "0";
      }
    },
    eta() {
      if (this.vesselDetailInfo && this.vesselDetailInfo.eta) {
        return this.vesselDetailInfo.eta.seconds !== null ||
          this.vesselDetailInfo.eta.seconds !== undefined
          ? DateUtil.timestampToYMDHm_jp(this.vesselDetailInfo.eta.seconds)
          : "---";
      } else {
        return "---";
      }
    },
    vesselCategoryName() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.c)
          ? this.vesselCategory.properties[this.vesselDetailInfo.c].name_ja
          : "---";
      } else {
        return "---";
      }
    },
    lastReceived() {
      if (this.vesselDetailPosition) {
        return this.vesselDetailPosition.t !== undefined
          ? DateUtil.elapsedTimeByTimestamp(this.vesselDetailPosition.t)
          : "---";
      } else {
        return "---";
      }
    },
    vesselOperations() {
      if (this.vesselDetailPosition) {
        const pos = this.vesselDetailPosition;
        if (pos == null || pos.s == null) {
          return "---";
        }
        return this.NAVIGATION_STATUS.properties[pos.s].name_ja;
      } else {
        return "---";
      }
    },
    speed() {
      if (this.vesselDetailPosition) {
        return this.vesselDetailPosition.v !== undefined
          ? this.vesselDetailPosition.v
          : "0.0";
      } else {
        return "0.0";
      }
    },
    lat() {
      if (this.vesselDetailPosition) {
        return this.vesselDetailPosition.l !== undefined
          ? `N${GeoUtil.toSexagesimalScale(this.vesselDetailPosition.l._lat)}`
          : "---";
      } else {
        return "---";
      }
    },
    lng() {
      if (this.vesselDetailPosition) {
        return this.vesselDetailPosition.l !== undefined
          ? `E${GeoUtil.toSexagesimalScale(this.vesselDetailPosition.l._long)}`
          : "---";
      } else {
        return "---";
      }
    },
    imo() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.i)
          ? this.vesselDetailInfo.i
          : "---";
      } else {
        return "---";
      }
    },
    callSign() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.cs)
          ? this.vesselDetailInfo.cs
          : "---";
      } else {
        return "---";
      }
    },
    mmsi() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.m)
          ? this.vesselDetailInfo.m
          : "---";
      } else {
        return "---";
      }
    },
    countryName() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.cc)
          ? this.getCountryName(this.vesselDetailInfo.cc)
          : "---";
      } else {
        return "---";
      }
    },
    length() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.l)
          ? `${this.vesselDetailInfo.l}m`
          : "---";
      } else {
        return "---";
      }
    },
    width() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.w)
          ? `${this.vesselDetailInfo.w}m`
          : "---";
      } else {
        return "---";
      }
    },
    fullDraft() {
      if (this.vesselDetailInfo) {
        return !this.isNull(this.vesselDetailInfo.fd)
          ? `${this.vesselDetailInfo.fd}m`
          : "---";
      } else {
        return "---";
      }
    },
    grossTonnage() {
      if (this.vesselDetail) {
        return !this.isNull(this.vesselDetail.vessel_size.gt)
          ? `${this.vesselDetail.vessel_size.gt}t`
          : "---";
      } else {
        return "---";
      }
    },
    sortedFavoriteList() {
      return this.favoriteList.map((favorite) => {
        return {
          label: favorite.label,
          groupList: favorite.groupList
            .map((group) => {
              return group;
            })
            .sort((a, b) => {
              return a.sort - b.sort;
            }),
        };
      });
    },
    isFreeAccount() {
      return this.$store.state.isFree === true;
    },
  },
  watch: {
    vesselDetail(newVal) {
      if (newVal) {
        this.SET_SELECTED_FIXED_POINT_CAMERA(null);
        this.show = true;
        this.switch = this.isMonitoring(newVal.id);
        this.activeItem = "image";
        this.$vs.loading.close(
          "#sidebar-vessel-detail-wrapper.vs-con-loading__container > .con-vs-loading",
        );
      } else {
        this.$vs.loading({
          container: "#sidebar-vessel-detail-wrapper.vs-con-loading__container",
        });
      }
    },
    monitoringState(newVal) {
      this.switch = newVal;
    },
    activeItem(newVal) {
      if (newVal === "radar") {
        this.styleObject.height = "120px";
      } else {
        this.styleObject.height = "300px";
      }
    },
  },
  methods: {
    ...mapMutations(["SET_SELECTED_FIXED_POINT_CAMERA"]),
    ...mapActions([
      "cleanUpVesselDetail",
      "subscribeVesselDetailInfo",
      "subscribeVesselDetailPosition",
      "addMonitoringVessel",
      "removeMonitoringVessel",
      "addVesselToGroup",
      "deleteVesselFromGroup",
    ]),
    setActive(isActive) {
      this.active = isActive;
    },
    onSwitchMedia(val) {
      this.activeItem = val;
    },
    isNull(val) {
      return val === undefined || val === null;
    },
    clickGroupChip(
      vesselId,
      favoriteKey,
      groupDocumentId,
      isVesselAlreadyAdded,
    ) {
      if (isVesselAlreadyAdded) {
        this.deleteVesselFromGroup({ vesselId, favoriteKey, groupDocumentId });
      } else {
        this.addVesselToGroup({ vesselId, favoriteKey, groupDocumentId });
      }
    },
  },
  mounted() {
    this.$vs.loading({
      container: "#sidebar-vessel-detail-wrapper.vs-con-loading__container",
    });
  },
  beforeDestroy() {
    if (this.unsubscribes && this.unsubscribes.length !== 0) {
      this.unsubscribes.forEach((unsubscribe) => unsubscribe());
      this.unsubscribes = [];
    }
  },
};
</script>

<style lang="scss">
#sidebar-vessel-detail {
  .swiper-button-prev,
  .swiper-button-next {
    filter: drop-shadow(0px 2px 3px black);
    height: 2rem;
    top: 55%;
    z-index: 200;
  }
}
.sidebar-vessel-detail-loading__container {
  position: relative;
  width: 100%;
  height: 100%;
}
.sidebar-vessel-detail-scroll-area {
  position: relative;
  margin: auto;
  width: 250px;
  //max-height: 300px;
  max-height: 210px;
  //height: calc(90vh - 400px) !important;
  height: calc(90vh - 310px) !important;
  pointer-events: auto;
  overflow-y: scroll;
  overflow-x: hidden;
}

.sidebar-vessel-detail__container {
  pointer-events: auto;
  overflow-y: scroll;
  overflow-x: hidden;
}

.vessel-picture-img {
  width: 100%;
  height: 140px;
}

.parentx-static {
  padding-top: 50px;
  overflow: hidden;
  height: 750px;
  top: 30px;
  left: 10px;
  position: relative;
}
#sidebar-vessel-detail {
  .camera .video-js {
    height: 140px;
  }
  .radar .video-js {
    height: 320px;
  }
}

.feather-icon-color {
  color: #1e1e1e;
}
.footer-sidebar {
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  > button {
    border: 0px solid rgba(0, 0, 0, 0) !important;
    border-left: 1px solid rgba(0, 0, 0, 0.07) !important;
    border-radius: 0px !important;
  }
}
.footer-scroll-area {
  overflow: hidden;
  overflow-y: scroll;
  padding: 0px 18px;
  height: 110px;
  .group-container {
    margin: 10px 0px;
    .group-chip-area {
      display: flex;
      flex-wrap: wrap;
      margin-top: 5px;
      .group-chip {
        display: flex;
        position: relative;
        align-items: center;
        border: 1px solid #e1e1e1;
        border-radius: 15px;
        font-size: 10px;
        padding: 3px 8px;
        margin-bottom: 7px;
        margin-right: 7px;
        transition: 0.2s;
        cursor: pointer;
        .chip-label {
          font-size: 12px;
        }
        .chip-circle {
          width: 8px;
          height: 8px;
          margin-right: 5px;
          border-radius: 10px;
          background: var(--color);
        }

        .chip-background {
          width: 100%;
          height: 100%;
          position: absolute;
          margin-left: -8px;
          border-radius: 15px;
          background: var(--color);
          opacity: 0;
        }

        &:hover {
          color: var(--color);
          border-color: var(--color);
        }
        &.selected {
          font-weight: bold;
          color: var(--color);
          border-color: var(--color);
          .chip-background {
            opacity: 0.4;
          }
        }
      }
    }
  }
}
#sidebar-vessel-detail-wrapper {
  max-width: 260px;
  max-height: 650px;
  width: 100%;
  height: 100%;
}
</style>
